import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  SxProps,
  Theme,
} from "@mui/material";
import Image from "next/image";
import { ReactNode, useCallback, useState } from "react";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import { EditIcon, TrashIcon } from "@/components/common/icons";
import { ERROR_PRIMARY, GREY, VIOLET, WHITE } from "@/config/mui/colorPalette";
import { useCanConfigureMedspa } from "@/hooks/user/useIsProviderOwner";

type Props = {
  image: string;
  aspectRatio: string;
  onEdit?: () => void;
  onDelete?: () => void;
  sx?: SxProps<Theme>;
};

export default function BannerImageContainer({
  image,
  aspectRatio,
  onEdit,
  onDelete,
  sx,
}: Props) {
  const canConfigureMedspa = useCanConfigureMedspa();
  const { newPermissionsEnabledForUser, hasFeaturePermission } = useUser();
  const canEditOnlineStoreSettings = newPermissionsEnabledForUser
    ? hasFeaturePermission(FeaturePermission.EDIT_ONLINE_STORE_SETTINGS)
    : canConfigureMedspa;

  const [hasImageLoaded, setHasImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const onLoadCompleteCallback = useCallback(() => {
    setHasImageLoaded(true);
  }, [setHasImageLoaded]);

  const onErrorCallback = useCallback(() => {
    setImageError(true);
  }, [setImageError]);

  return (
    <Box
      sx={{
        position: "relative",
        aspectRatio: aspectRatio,
        borderRadius: "8px",
        overflow: "hidden",
        ...sx,
      }}
    >
      <Image
        fill
        src={image}
        alt="Medspa Banner"
        style={{ objectFit: "cover" }}
        onLoad={onLoadCompleteCallback}
        onError={onErrorCallback}
      />
      {hasImageLoaded && (
        <Stack
          sx={{
            gap: 0.5,
            top: "50%",
            right: "8px",
            position: "absolute",
            flexDirection: "row",
            transform: "translateY(-50%)",
          }}
        >
          {onEdit && (
            <ButtonWithIcon
              icon={<EditIcon color={VIOLET[90]} size={24} />}
              disabled={!canEditOnlineStoreSettings}
              onClick={onEdit}
            />
          )}
          {onDelete && (
            <ButtonWithIcon
              icon={<TrashIcon color={ERROR_PRIMARY} size={24} />}
              disabled={!canEditOnlineStoreSettings}
              onClick={onDelete}
            />
          )}
        </Stack>
      )}
      {!hasImageLoaded && !imageError && <BannerLoader />}
    </Box>
  );
}

const BannerLoader = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: GREY[15],
      }}
    >
      <CircularProgress size="36px" color="secondary" />
    </Box>
  );
};

const ButtonWithIcon = ({
  icon,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  disabled: boolean;
  onClick: () => void;
}) => {
  return (
    <IconButton
      onClick={onClick}
      disabled={disabled}
      sx={{
        p: 0.5,
        bgcolor: WHITE,
        borderRadius: "8px",
        "&:hover": {
          bgcolor: WHITE,
        },
      }}
    >
      {icon}
    </IconButton>
  );
};
